import type { Pagination, UUIDString } from '@aigc/types';
import type { RemovalBillingData, RemovalProvider } from '../credit/model';
import type {
  BatchTranslateImageInputDto,
  GenerateImageInputDto,
  TranslateImageInputDto,
} from '../image/model';
import type {
  ProductAnalysisInputDto,
  ProductAnalysisOutputDto,
} from '../product-analysis/model';
import type { ScriptDto } from '../script/script.dto';
import type { UserDto } from '../user/models';
import type { VideoModelCode } from '../video-model/model-spec';
import type { VideoReplicationInputDto } from '../video-replication/model';
import type {
  LagacyTranslateVideoDataDto,
  VideoTranslationOutputDto,
} from '../video-translation/model';

export const enum TaskStatusCode {
  未开始 = 'not_started',

  校验图片 = 'check_image',
  上传资源 = 'upload_asset',
  分析视频 = 'analysis',

  翻译图片 = 'translate_image',

  分析视频2 = 'analyze_video',
  处理资源 = 'handle_asset',
  校验资源 = 'check_asset',

  生成视频 = 'generate_video',
  任务执行中 = 'running',

  视频编辑准备 = 'video_edit_prepare',
  视频编辑合成 = 'video_edit_merge',
  画质提升 = 'upscaler',

  任务成功 = 'succeeded',
  任务失败 = 'failed',
}

export const taskStatusTextMap = new Map<TaskStatusCode, string>([
  [TaskStatusCode.未开始, '任务创建中'],
  [TaskStatusCode.校验图片, '图片校验中'],
  [TaskStatusCode.上传资源, '资源上传中'],
  [TaskStatusCode.分析视频, '视频分析中'],
  [TaskStatusCode.翻译图片, '图片翻译中'],
  [TaskStatusCode.分析视频2, '视频分析中'],
  [TaskStatusCode.处理资源, '资源处理中'],
  [TaskStatusCode.校验资源, '资源校验中'],
  [TaskStatusCode.生成视频, '视频生成中'],
  [TaskStatusCode.任务执行中, '生成中'],
  [TaskStatusCode.视频编辑准备, '编辑准备中'],
  [TaskStatusCode.视频编辑合成, '视频合成中'],
  [TaskStatusCode.任务成功, '生成成功'],
  [TaskStatusCode.任务失败, '生成失败'],
  [TaskStatusCode.画质提升, '视频生成中'],
]);

export interface UpscalerTemplateDto {
  template_id: string;
  name: string;
  run_id: string;
  status: string;
  url: string;
}

export interface UpscalerResultDto {
  provider: string;
  vid: string;
  templates?: UpscalerTemplateDto[];
}

export interface TaskResultDto {
  /**
   * 各个平台返回的任务ID，可以通过这个id，到各个平台查询任务状态
   *
   * 生成视频失败的时候，一般id为空
   */
  id?: UUIDString;
  /**
   * 任务状态
   */
  status: TaskStatusCode;
  /**
   * 大语言模型
   */
  model?: VideoModelCode;
  /**
   * 视频文件名/图片文件名
   */
  filename?: string;
  /**
   * 用量
   */
  usage?: Record<string, number>;
  /**
   * 错误信息
   */
  error?: string;
  /**
   * 时长
   */
  duration?: number;
  /**
   * 创建时间，单位为毫秒
   */
  created_at?: number;
  /**
   * 更新时间，单位为毫秒
   */
  updated_at?: number;
  /**
   * 视频画质增强结果
   */
  upscaler?: UpscalerResultDto;
}

export const enum TaskType {
  视频生成 = 'video',
  图片生成 = 'image',
  长视频生成 = 'long_video',
  视频编辑 = 'video_edit',
  视频翻译_废弃 = 'translate',
  视频翻译 = 'long_video_translate',
  视频分析 = 'analyze_video',
  视频复刻 = 'video_replication',
  爆款生成 = 'product_analysis',
  脚本裂变 = 'script_fission',
  智能擦除 = 'video_smart_erase',
  画质修复 = 'video_quality_restoration',
  图片翻译 = 'translate_image',
  批量图片翻译 = 'batch_translate_image',
}

export interface ImageTaskListInputDto extends Pagination {
  name?: string;
  type?: TaskType.图片生成 | TaskType.图片翻译;
}

export interface ImageGenerationTaskListInputDto extends Pagination {
  /** 任务名称 */
  name?: string;
}

export interface ImageTranslationTaskListInputDto extends Pagination {
  /**
   * 任务名
   */
  name?: string;
}

export interface TaskListInputDto extends Pagination {
  task_ids?: string[];

  task_types?: TaskType[];

  /**
   * 任务状态列表
   */
  status?: TaskStatusCode[];
}

export interface BaseTaskDto {
  /**
   * 主键ID，short_code
   */
  id: UUIDString;
  /**
   * 创建人
   */
  creator?: UserDto | null;
  /**
   * 任务类型 video/translate
   */
  type: TaskType;
  /**
   * 任务状态 no_start, running, succeeded, failed
   */
  status: TaskStatusCode;
  /**
   * 更新时间戳(毫秒)
   */
  updated_at?: EpochTimeStamp;
  /**
   * 创建时间戳(毫秒)
   */
  created_at: EpochTimeStamp;
  // 下面是关于视频生成的字段
  /**
   * 各个视频生成平台的任务ID，用于查询视频状态，TaskResult中的id
   */
  task_id?: string;
  /**
   * 脚本ID，指向 Scripts 表的 id 字段
   */
  script_id?: string;
  /**
   * 模型名称
   */
  model: VideoModelCode;
  /**
   * 视频时长
   */
  duration: number;
  /**
   * 错误信息
   */
  error?: string;
  /**
   * 视频文件名/图片文件名
   */
  filename?: string;
  /**
   * 视频
   */
  dna?: string;
}

/**
 * 视频翻译任务
 *
 * @deprecated 原视频翻译任务，只能支持 15 秒以内的翻译，已废弃，不可使用，旧数据还在
 */
export interface LegacyVideoTranslationTaskDto extends BaseTaskDto {
  type: TaskType.视频翻译_废弃;
  /**
   * 视频生成数据(JSON字符串)
   */
  generate_data?: ScriptDto & {
    translate_options: Omit<LagacyTranslateVideoDataDto, 'video_url'>;
  };
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

/**
 * 视频翻译任务
 */
export interface VideoTranslationTaskDto extends BaseTaskDto {
  removal_provider?: RemovalProvider | null;
  type: TaskType.视频翻译;
  /**
   * 视频生成数据(JSON字符串)
   */
  generate_data?: VideoTranslationOutputDto & ScriptDto;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

/**
 * 视频生成任务
 */
export interface VideoGenerationTaskDto extends BaseTaskDto {
  type: TaskType.视频生成 | TaskType.长视频生成;
  /**
   * 视频生成数据(JSON字符串)
   */
  generate_data?: ScriptDto;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

/**
 * 视频分析任务
 */
export interface VideoAnalysisTaskDto extends BaseTaskDto {
  type: TaskType.视频分析;
  /**
   * 视频分析数据(JSON字符串)
   */
  generate_data?: ScriptDto;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

/** 视频复刻脚本生成任务。 */
export interface VideoReplicationTaskDto extends BaseTaskDto {
  type: TaskType.视频复刻;
  generate_data?: VideoReplicationInputDto;
  result?: ScriptDto;
}

/**
 * 爆款生成任务
 */
export interface ProductAnalysisTaskDto extends BaseTaskDto {
  type: TaskType.爆款生成;
  generate_data?: ProductAnalysisInputDto;
  result?: ProductAnalysisOutputDto;
}

export interface ScriptFissionTaskDto extends BaseTaskDto {
  type: TaskType.脚本裂变;
  generate_data: ScriptDto | null;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

export interface SmartEraseTaskDto extends BaseTaskDto {
  removal_provider?: RemovalProvider | null;
  type: TaskType.智能擦除;
  generate_data:
    ({ video_url: string } & ScriptDto & RemovalBillingData) | null;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

export interface VideoQualityRestorationTaskDto extends BaseTaskDto {
  type: TaskType.画质修复;
  generate_data: ({ video_url: string } & ScriptDto) | null;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

export interface VideoEditTaskDto extends BaseTaskDto {
  type: TaskType.视频编辑;
  /**
   * 视频编辑数据(JSON字符串)
   */
  generate_data?: ScriptDto;
  /**
   * 视频生成结果
   */
  result?: TaskResultDto;
}

export interface ImageGenerationTaskDto extends BaseTaskDto {
  type: TaskType.图片生成;
  /**
   * 图片生成数据
   */
  generate_data?: GenerateImageInputDto;
  /**
   * 生成结果
   */
  result?: {
    image_urls: string[];
  };
}

export interface ImageTranslationTaskDto extends BaseTaskDto {
  type: TaskType.图片翻译;
  /**
   * 图片生成数据
   */
  generate_data: TranslateImageInputDto;
  /**
   * 生成结果
   */
  result?: {
    image_url: string;
  };
}

export interface BatchImageTranslationTaskDto extends BaseTaskDto {
  type: TaskType.批量图片翻译;

  generate_data: BatchTranslateImageInputDto;

  children?: ImageTranslationTaskDto[];
}

export type VideoTaskDto =
  | VideoGenerationTaskDto
  | VideoEditTaskDto
  | LegacyVideoTranslationTaskDto
  | VideoTranslationTaskDto
  | VideoAnalysisTaskDto
  | ScriptFissionTaskDto
  | SmartEraseTaskDto
  | VideoQualityRestorationTaskDto;

export type ImageTaskDto =
  | ImageGenerationTaskDto
  | ImageTranslationTaskDto
  | BatchImageTranslationTaskDto;

export type TaskDto =
  | VideoGenerationTaskDto
  | VideoEditTaskDto
  | LegacyVideoTranslationTaskDto
  | VideoTranslationTaskDto
  | VideoAnalysisTaskDto
  | VideoReplicationTaskDto
  | ProductAnalysisTaskDto
  | ScriptFissionTaskDto
  | SmartEraseTaskDto
  | ImageGenerationTaskDto
  | VideoQualityRestorationTaskDto
  | ImageTaskDto;
